<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n';
import { useModalStore } from '@/stores/ModalStore';
import { useAppStore } from '@/stores/AppStore';
import { localizedRoutes } from '../../mixins/localization'
import { useNoticeStore } from '@/stores/NoticeStore'
import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import { useRouter } from 'vue-router'
import type { PropType } from 'vue';

const router = useRouter()

const { t } = useI18n();
const { openModal } = useModalStore();

const { addNotice } = useNoticeStore()
const appStore = useAppStore()
const { isSignedIn, getCurrentCustomer } = storeToRefs(appStore)

defineProps({
  state: {
    type: String,
    required: true,
  },

  addedClass: {
    type: String,
    default: '',
  },

  customPadding: {
    type: String,
    default: '',
  },

  iconButtonOnly: {
    type: Boolean,
    default: false,
  },

  product: {
    type: Object as PropType<{ id: string; permalink: string; name: string }>,
    required: true,
  },
  stretched: {
    type: Boolean,
    default: true,
  },
});

// types: 'link' | 'static' | 'modal'
const stateObject = (action: string) => {
  switch (action) {
    case 'only_for_club': {
        return {
            label: t('mixit_club_only'), // button label
            theme: 'warning', // button theme (color)
            themeText: 'white',
            icon: 'bird', // button icon
            type: 'link', // button type - if LINK then "linkTo" prop is required!
            linkTo: 'clubPath' // button link where to redirect
        }
    }
    case 'succeed': {
        return {
            label: t('good_deeds_succeed'),
            theme: 'info',
            icon: 'heart-fill',
            type: 'static',
            themeText: 'white',
        }
    }
    case 'weather_too_hot': {
        return {
            label: t('look_after'),
            theme: 'info',
            icon: 'frozen',
            type: 'modal',
            modalOpen: 'modal-watchdog',
            themeText: 'white',
        }
    }
    case 'look_after': {
        return {
          label: t('look_after'),
            theme: 'primary',
            icon: 'bird',
            type: 'modal',
            modalOpen: 'modal-watchdog',
            themeText: 'light',
        }
    }
    case 'revive_me': {
        return {
            label: t('revive_me'),
            theme: 'cemetery',
            icon: 'heart-fill',
            type: 'modal',
            modalOpen: 'modal-watchdog',
            themeText: 'white',
        }
    }
    case 'mix_me': {
        return {
            label: t('to_mix'),
            theme: 'warning',
            icon: 'mix',
            type: 'link',
            modalOpen: 'modal-watchdog',
            themeText: 'white',
            linkTo: 'mixesPath',
            permalink: 'path.own_musli'
        }
    }
    case 'hidden':
    case 'removed': {
        return {
            label: t('sale_closed'),
            theme: 'primary',
            icon: 'bird',
            type: 'static',
            themeText: 'light',
        }
    }
    default: {
        return {
            label: t('look_after'),
            theme: 'primary',
            icon: 'bird',
            type: 'modal',
            modalOpen: 'modal-watchdog',
            themeText: 'light',
        }
    }
  }
};

const watchdogSignedIn = async (product: { id: string; permalink: string; name: string }) => {
  const path = router
    .resolve({ name: 'productPath', params: { permalink: product.permalink } })
    .fullPath.slice(1)

  const productUrl = `${import.meta.env.VITE_VUE_APP_ORIGINAL_URL}${path}`
  try {
    const { data } = await ApiServiceV1.post('watchdogs', {
      watchdog: {
        product_id: product.id,
        product_url: productUrl,
        email: getCurrentCustomer?.value?.email,
      },
    })
    console.log('Watchdog signed in:', data)
    if (data.result === 'success') {
      addNotice({
        text: t('watchdog_signed_in', { name: product.name }),
        background: 'bg-warning',
      })
    } else {
      addNotice({
        text: t('something_went_wrong', { name: product.name }),
        background: 'bg-danger',
      })
    }
  } catch {
    addNotice({
      text: t('something_went_wrong', { name: product.name }),
      background: 'bg-danger',
    })
  }
}
</script>

<template>
  <!-- Button type: LINK -->
  <router-link
    v-if="stateObject(state).type === 'link'"
    :to="
      stateObject(state) && stateObject(state).permalink ?
        { name: stateObject(state).linkTo, params: { permalink: localizedRoutes(stateObject(state).permalink || '') } }
        : { name: stateObject(state).linkTo }
    "
    type="button"
    class="btn c-rounded-pill font-medium text-white whitespace-nowrap"
    :class="[addedClass, `btn-${stateObject(state).theme}`, customPadding ? customPadding : 'py-3 c-sm:c-py-2', {'stretched-link':stretched } ]"
  >
    <i :class="[`mi-${stateObject(state).icon} icon-20`, { 'mr-2': !iconButtonOnly }]" />
    <span
      v-if="!iconButtonOnly"
      class="inline"
    >{{ stateObject(state).label }}</span>
  </router-link>

  <!-- Button type: STATIC -->
  <div
    v-if="stateObject(state).type === 'static'"
    class="c-rounded-pill font-medium whitespace-nowrap text-center"
    :class="[addedClass, `bg-${stateObject(state).theme}`, `text-${stateObject(state).themeText}`, customPadding ? customPadding : 'p-3 c-sm:c-p-2']"
  >
    <i :class="[`mi-${stateObject(state).icon} icon-20`, { 'mr-2': !iconButtonOnly }]" />
    <span
      v-if="!iconButtonOnly"
      class="inline"
    >{{ stateObject(state).label }}</span>
  </div>

  <!-- Button type: MODAL -->
  <button
    v-if="stateObject(state).type === 'modal'"
    type="button"
    class="btn c-rounded-pill font-medium whitespace-nowrap"
    :class="[addedClass, `btn-${stateObject(state).theme}`, `text-${stateObject(state).themeText}`, customPadding ? customPadding : 'p-3 c-sm:c-p-2', {'stretched-link':stretched }]"
    aria-label="Watchdog"
    @click="isSignedIn ? watchdogSignedIn(product) : openModal({ id: stateObject(state).modalOpen, params: { product } })"
  >
    <i :class="[`mi-${stateObject(state).icon} icon-20`, { 'mr-2': !iconButtonOnly }]" />
    <span
      v-if="!iconButtonOnly"
      class="inline"
    >{{ stateObject(state).label }}</span>
  </button>
</template>

<i18n>
  {
    "cs": {
    "good_deeds_succeed": "Podařilo se",
    "look_after": "Pohlídat",
    "mixit_club_only": "Mixit Klub",
    "revive_me": "Oživ mě",
    "sale_closed": "Prodej ukončen",
    "something_went_wrong": "Něco se pokazilo při hlídání produktu {name}",
    "to_mix": "Do mixu",
    "watchdog_signed_in": "Hlídání produktu {name} bylo úspěšně aktivováno."
},
    "sk": {
    "good_deeds_succeed": "Podarilo sa",
    "look_after": "Postrážiť",
    "mixit_club_only": "Mixit Klub",
    "revive_me": "Oživ ma",
    "sale_closed": "Predaj ukončený",
    "something_went_wrong": "Niečo sa pokazilo pri strážení produktu {name}",
    "to_mix": "Do mixu",
    "watchdog_signed_in": "Stráženie produktu {name} bolo úspešne aktivované."
},
    "pl": {
    "good_deeds_succeed": "Udało się",
    "look_after": "Obserwuj",
    "mixit_club_only": "Klub Mixit",
    "revive_me": "Ożyw mnie",
    "sale_closed": "Sprzedaż zakończona",
    "something_went_wrong": "Coś poszło nie tak podczas pilnowania produktu {name}",
    "to_mix": "Do mieszania",
    "watchdog_signed_in": "Pilnowanie produktu „ {name} ” zostało pomyślnie aktywowane."
},
    "hu": {
    "good_deeds_succeed": "Sikeres",
    "look_after": "Figyelni",
    "mixit_club_only": "Mixit klub",
    "revive_me": "Élessz fel",
    "sale_closed": "Eladás lezárva",
    "something_went_wrong": "Valami hiba történt a {name} termék megfigyelése közben",
    "to_mix": "Mixbe",
    "watchdog_signed_in": "{name} termék figyelése sikeresen aktiválva."
},
    "de": {
    "good_deeds_succeed": "Es hat geklappt",
    "look_after": "Siehe",
    "mixit_club_only": "Mixit-Club",
    "revive_me": "Wiederherstellen",
    "sale_closed": "Verkauf beendet.",
    "something_went_wrong": "Beim Beobachten des Produkts ist leider etwas schiefgelaufen {name}",
    "to_mix": "mixen",
    "watchdog_signed_in": "Die Produktbeobachtung für „ {name} “ wurde erfolgreich aktiviert."
},
    "at": {
    "good_deeds_succeed": "Es hat geklappt",
    "look_after": "Siehe",
    "mixit_club_only": "Mixit-Club",
    "revive_me": "Wiederherstellen",
    "sale_closed": "Verkauf beendet.",
    "something_went_wrong": "Beim Beobachten des Produkts ist leider etwas schiefgelaufen {name}",
    "to_mix": "mixen",
    "watchdog_signed_in": "Die Produktbeobachtung für „ {name} “ wurde erfolgreich aktiviert."
},
    "ro": {
    "good_deeds_succeed": "Am reușit",
    "look_after": "Notifică",
    "mixit_club_only": "Club Mixit",
    "revive_me": "Învie-mă",
    "sale_closed": "Vânzare încheiată",
    "something_went_wrong": "Ceva nu a mers bine la monitorizarea produsului {name}",
    "to_mix": "În amestec",
    "watchdog_signed_in": "Monitorizarea produsului {name} a fost activată cu succes."
},
    "fr": {
    "good_deeds_succeed": "C’est réussi",
    "look_after": "Surveiller",
    "mixit_club_only": "Mixit Club",
    "revive_me": "Redonne-moi vie",
    "sale_closed": "Vente terminée",
    "something_went_wrong": "Quelque chose s’est mal passé lors du suivi du produit {name}",
    "to_mix": "Dans le mix",
    "watchdog_signed_in": "Le suivi du produit {name} a été activé avec succès."
},
    "it": {
    "good_deeds_succeed": "Fatto",
    "look_after": "Monitora",
    "mixit_club_only": "Mixit Club",
    "revive_me": "Ravvivami",
    "sale_closed": "Vendita terminata",
    "something_went_wrong": "Si è verificato un errore durante il monitoraggio del prodotto{name}",
    "to_mix": "Al mix",
    "watchdog_signed_in": "Monitoraggio del prodotto{name} è stato attivato con successo."
}
  }
</i18n>